import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Política de retención de datos — PLANTILLA base.
 * Balancea el deber de archivo público (Ley 594/2000) con el derecho
 * al olvido y principio de finalidad (Ley 1581/2012).
 * Cada alcaldía ajusta las cifras con su área jurídica y de archivo central.
 */
export function DocsRetencionPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Política de retención
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Política de retención de datos
        </h1>

        <div className="callout warn">
          <strong>Las cifras son referenciales.</strong> Cada alcaldía debe
          ajustarlas según las tablas de retención documental aprobadas por su
          comité interno de archivo y validadas con su área jurídica.
        </div>

        {/* ---- 1 ---- */}
        <Section title="1. Por qué importa">
          <p>
            La política de retención busca equilibrar tres deberes que pueden
            entrar en tensión:
          </p>
          <Ul
            items={[
              <>
                <strong>Deber de archivo público</strong> (Ley 594/2000):
                conservar la memoria documental del Estado y permitir el
                ejercicio del derecho a la información.
              </>,
              <>
                <strong>Derecho al olvido y principio de finalidad</strong>{" "}
                (Ley 1581/2012, Art. 4): no conservar datos personales más allá
                del tiempo necesario para la finalidad del tratamiento.
              </>,
              <>
                <strong>Transparencia y rendición de cuentas</strong> (Ley
                1712/2014): garantizar trazabilidad de las decisiones públicas.
              </>,
            ]}
          />
          <p>
            La retención excesiva expone a la entidad a sanciones de la SIC.
            La eliminación prematura impide el cumplimiento de obligaciones
            legales de archivo y rendición de cuentas. Esta política define los
            tiempos máximos y mínimos para cada categoría de información.
          </p>
        </Section>

        {/* ---- 2 ---- */}
        <Section title="2. Tabla de retención">
          <Table
            head={["Dato", "Tiempo de conservación", "Fundamento"]}
            rows={[
              [
                "PQRSDF radicadas y sus respuestas",
                "10 años desde el cierre",
                "Archivo histórico estatal — Ley 594/2000 y tablas de retención documental",
              ],
              [
                "Adjuntos vinculados a una PQRSDF",
                "10 años desde el cierre",
                "Acompañan al expediente principal",
              ],
              [
                "Chats ciudadano informales (sin radicado)",
                "2 años desde el cierre, luego anonimización",
                "Finalidad limitada a mejora del servicio",
              ],
              [
                "Transcripciones de chat con bot RAG",
                "1 año, luego anonimización",
                "Mejora del modelo y trazabilidad",
              ],
              [
                "Audit log (trazabilidad de accesos y cambios)",
                "5 años",
                "Auditoría interna y entes de control (Procuraduría, Contraloría)",
              ],
              [
                "Datos del peticionario sin radicados activos",
                "1 año desde último uso, luego anonimización o borrado",
                "Principio de finalidad — Art. 4 lit. e) Ley 1581/2012",
              ],
              [
                "Cuentas de funcionarios desvinculados",
                "Deshabilitación inmediata + 2 años en estado archivado",
                "Trazabilidad de acciones realizadas durante su vinculación",
              ],
              [
                "Backups operacionales",
                "90 días en rotación, luego sobrescritura automática",
                "Recuperación ante desastres",
              ],
              [
                "Logs operacionales (aplicación, infraestructura)",
                "30 días",
                "Diagnóstico técnico y monitoreo de seguridad",
              ],
              [
                "Métricas anonimizadas y agregadas",
                "Indefinido",
                "No contienen datos personales identificables",
              ],
              [
                "Encuestas de satisfacción",
                "3 años, luego anonimización",
                "Análisis histórico de calidad del servicio",
              ],
            ]}
          />
        </Section>

        {/* ---- 3 ---- */}
        <Section title="3. Anonimización vs eliminación">
          <p>
            La plataforma distingue dos operaciones de cierre del ciclo de
            vida del dato. La elección depende de si existe obligación legal
            de conservar trazabilidad o no.
          </p>
          <Table
            head={["Operación", "Qué hace", "Cuándo se usa"]}
            rows={[
              [
                "Anonimización",
                "Reemplaza datos identificables (nombre, documento, email, teléfono, dirección IP) por valores irreversibles tipo hash o NULL. Conserva el registro estadístico (categoría, fecha, dependencia, SLA cumplido).",
                "Cuando la entidad debe mantener trazabilidad histórica o estadística pero ya no requiere identificar al titular (PQRSDF vencidas, chats cerrados pasada la finalidad).",
              ],
              [
                "Eliminación",
                "Borrado físico del registro y de sus referencias. Solo queda una marca en el audit log con el número de operación y la causal (sin datos personales).",
                "Cuando el titular ejerce el derecho de supresión y no existe causal legal para conservar; cuando la finalidad ya no aplica y no hay obligación de archivo (ejemplo: cuentas de prueba, datos cargados por error).",
              ],
            ]}
          />
        </Section>

        {/* ---- 4 ---- */}
        <Section title="4. Procesos automáticos (cron jobs)">
          <p>
            La plataforma ejecuta tareas programadas que aplican la política
            sin intervención manual:
          </p>
          <Table
            head={["Tarea", "Frecuencia", "Acción"]}
            rows={[
              [
                "anonimizar_chats_vencidos",
                "Diaria — 02:00 Bogotá",
                "Anonimiza chats informales cerrados hace más de 2 años",
              ],
              [
                "purgar_peticionarios_inactivos",
                "Mensual — primer día del mes",
                "Marca para revisión los peticionarios sin radicados activos por más de 1 año",
              ],
              [
                "rotar_backups",
                "Diaria — 04:00 Bogotá",
                "Elimina backups con antigüedad mayor a 90 días",
              ],
              [
                "rotar_logs_operacionales",
                "Diaria — 03:00 Bogotá",
                "Elimina logs operacionales con antigüedad mayor a 30 días",
              ],
              [
                "anonimizar_encuestas_vencidas",
                "Mensual",
                "Anonimiza respuestas de encuestas con antigüedad mayor a 3 años",
              ],
              [
                "alertar_pqrsdf_vencidas_archivo",
                "Anual — enero",
                "Reporta PQRSDF que cumplieron 10 años para revisión del comité de archivo",
              ],
            ]}
          />
          <p>
            Cada ejecución queda registrada en el audit log con: tarea, fecha,
            cantidad de registros procesados, errores. El reporte se envía al
            <code> admin_tenant</code> mensualmente.
          </p>
        </Section>

        {/* ---- 5 ---- */}
        <Section title="5. Excepciones a la retención estándar">
          <p>
            Existen situaciones en las que los plazos anteriores se suspenden
            o extienden. En estos casos, los datos no se anonimizan ni
            eliminan hasta que cese la causal:
          </p>
          <Ul
            items={[
              <>
                <strong>Litigios en curso:</strong> cuando exista demanda,
                investigación disciplinaria o fiscal en la que la información
                sea prueba, debe conservarse hasta la firmeza de la decisión.
              </>,
              <>
                <strong>Requerimientos judiciales:</strong> orden expresa de
                autoridad judicial o administrativa competente que exija la
                conservación.
              </>,
              <>
                <strong>Acciones de tutela o constitucionales:</strong>{" "}
                conservación durante el trámite y la ejecución del fallo.
              </>,
              <>
                <strong>Investigaciones de la SIC u otros entes de control:</strong>{" "}
                conservación durante el procedimiento.
              </>,
              <>
                <strong>Solicitud expresa del titular</strong> de conservar
                datos por interés propio (ejemplo: histórico de
                certificaciones).
              </>,
            ]}
          />
          <p>
            La aplicación de una excepción debe estar documentada en el
            expediente y registrada en la plataforma marcando el registro como{" "}
            <code>retencion_extendida</code> con la causal y la fecha
            estimada de cese.
          </p>
        </Section>

        {/* ---- 6 ---- */}
        <Section title="6. Procedimiento de purga manual">
          <p>
            Cuando se requiera ejecutar una purga fuera de los procesos
            automáticos (ejemplo: solicitud de supresión por habeas data o
            depuración por instrucción del comité de archivo):
          </p>
          <Ol>
            <li>
              <strong>Solicitud formal</strong>: el solicitante (titular,
              admin_tenant o comité de archivo) presenta la solicitud por
              escrito con justificación.
            </li>
            <li>
              <strong>Validación de competencia</strong>: el oficial de
              protección de datos verifica que quien solicita está facultado y
              que no aplica ninguna causal de excepción de la sección 5.
            </li>
            <li>
              <strong>Aprobación documentada</strong>: el admin_tenant aprueba
              la purga dejando constancia escrita (acta o memorando interno).
            </li>
            <li>
              <strong>Ejecución</strong>: a través del panel de administración
              con doble confirmación. El sistema solicita motivo,
              identificación del responsable y referencia del acto que la
              autoriza.
            </li>
            <li>
              <strong>Registro en audit log</strong>: la plataforma registra
              automáticamente qué se borró, cuándo, quién lo autorizó y bajo
              qué número de solicitud o acta.
            </li>
            <li>
              <strong>Notificación</strong>: si la purga fue solicitada por el
              titular, se le notifica el cumplimiento dentro de los plazos
              legales.
            </li>
          </Ol>
        </Section>

        {/* ---- 7 ---- */}
        <Section title="7. Auditoría de la purga">
          <p>
            Toda operación de eliminación o anonimización queda registrada de
            forma <strong>inmutable</strong> en el audit log con los siguientes
            campos mínimos:
          </p>
          <Ul
            items={[
              "Fecha y hora exactas (UTC + zona horaria local).",
              "Tipo de operación (anonimización / eliminación).",
              "Categoría del registro afectado (PQRSDF, chat, peticionario, adjunto, etc.).",
              "Identificador del registro (no los datos personales).",
              "Causal (cron automático, solicitud de titular, instrucción de archivo, requerimiento judicial).",
              "Usuario que autorizó (o sistema, si fue automática).",
              "Referencia documental (radicado de solicitud, acta, oficio).",
            ]}
          />
          <p>
            El audit log <strong>no contiene los datos personales eliminados</strong>;
            solo la evidencia de que la operación ocurrió. Se conserva 5 años.
          </p>
        </Section>

        {/* ---- 8 ---- */}
        <Section title="8. Eliminación anticipada a solicitud del ciudadano">
          <p>
            El ciudadano puede solicitar la eliminación de sus datos antes de
            que se cumpla el plazo de retención estándar. El procedimiento
            está descrito en la{" "}
            <Link to="/docs/habeas-data">
              Política de privacidad y Habeas Data
            </Link>{" "}
            (sección 12). La solicitud puede ser parcial o total y se evalúa
            caso por caso contra las causales de excepción.
          </p>
          <div className="callout">
            <strong>Importante:</strong> si la PQRSDF está dentro del periodo
            obligatorio de archivo (10 años), no procede la eliminación pero sí
            la anonimización de datos no esenciales para el expediente. La
            entidad debe explicar al ciudadano por qué no puede borrar el
            registro completo.
          </div>
        </Section>

        {/* ---- 9 ---- */}
        <Section title="9. Retención extendida a solicitud de la alcaldía">
          <p>
            La alcaldía puede solicitar conservar datos más allá del plazo
            estándar por razones legítimas (estudios estadísticos, valoración
            histórica, procesos contractuales en curso). El procedimiento es:
          </p>
          <Ol>
            <li>
              <strong>Acta interna</strong> firmada por el admin_tenant y el
              responsable del área solicitante (comité de archivo, secretaría
              jurídica) justificando la prórroga.
            </li>
            <li>
              <strong>Configuración en el panel</strong>: cambiar la regla de
              retención del tipo de registro afectado con la nueva fecha y
              adjuntando el acta como soporte.
            </li>
            <li>
              <strong>Notificación a MiPueblo</strong> a través de soporte
              cuando se requiera ajuste técnico.
            </li>
            <li>
              <strong>Revisión anual</strong> de las prórrogas vigentes para
              evaluar si la causal subsiste.
            </li>
          </Ol>
        </Section>

        {/* ---- 10 ---- */}
        <Section title="10. Reporte anual de retención">
          <p>
            Cada año, en el mes de enero, la plataforma genera un reporte
            consolidado del periodo anterior con:
          </p>
          <Ul
            items={[
              "Total de registros eliminados por categoría.",
              "Total de registros anonimizados por categoría.",
              "Total de prórrogas activas con su causal.",
              "Total de solicitudes de supresión recibidas vs ejecutadas vs negadas.",
              "Incidentes de seguridad relacionados con datos personales (si los hubo).",
              "Recomendaciones del oficial de protección de datos para el siguiente periodo.",
            ]}
          />
          <p>
            El reporte queda archivado en el tenant y debe ser revisado por el
            admin_tenant y el comité interno de archivo. Sirve como insumo
            para el reporte anual ante la SIC en el Registro Nacional de Bases
            de Datos (RNBD).
          </p>
        </Section>

        <div className="callout warn" style={{ marginTop: 32 }}>
          <strong>Recordatorio final:</strong> los plazos aquí descritos son
          referenciales y deben ajustarse a las tablas de retención documental
          aprobadas por la alcaldía con concepto del comité interno de archivo
          y validación jurídica.
        </div>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
